import React, { useEffect, useRef } from "react";
import { MapPin, ShieldCheck, Store } from "lucide-react";

// Light "3D" hero scene built only with CSS transforms (no WebGL, no extra library, ~0 KB).
// A phone and glass cards float at different depths and tilt gently with the pointer.
// Static fallback: with "reduce motion", on very small/slow devices or without CSS 3D support,
// the same picture is shown flat and still (see .vlx-scene rules in index.css).
// The amount shown is clearly marked as an EXAMPLE, not a real price.
export const Hero3D = ({ t, count }) => {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const lowEnd = (navigator.deviceMemory && navigator.deviceMemory <= 1) || (navigator.hardwareConcurrency || 4) <= 1;
    if (reduce || lowEnd) { el.classList.add("vlx-static"); return undefined; }
    let raf = 0;
    const move = (x, y) => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        el.style.setProperty("--rx", `${(-y * 8).toFixed(2)}deg`);
        el.style.setProperty("--ry", `${(x * 12).toFixed(2)}deg`);
      });
    };
    const onPointer = (e) => {
      const r = el.getBoundingClientRect();
      move((e.clientX - r.left) / r.width - 0.5, (e.clientY - r.top) / r.height - 0.5);
    };
    const onLeave = () => move(0, 0);
    el.addEventListener("pointermove", onPointer);
    el.addEventListener("pointerleave", onLeave);
    return () => { cancelAnimationFrame(raf); el.removeEventListener("pointermove", onPointer); el.removeEventListener("pointerleave", onLeave); };
  }, []);

  return (
    <div ref={ref} data-testid="hero-3d" className="vlx-scene relative mx-auto h-[400px] w-full max-w-[460px] sm:h-[420px]" aria-hidden="true">
      <div className="vlx-stage">
        {/* glow */}
        <div className="absolute left-1/2 top-1/2 h-64 w-64 -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand/25 blur-3xl" />
        {/* phone */}
        <div className="vlx-layer vlx-phone" style={{ "--z": "40px" }}>
          <div className="h-full w-full rounded-[34px] bg-gradient-to-br from-[#1b2a4a] to-[#0A192F] p-[10px] shadow-2xl">
            <div className="relative h-full w-full overflow-hidden rounded-[26px] bg-gradient-to-b from-[#EAF2FF] to-white">
              <div className="mx-auto mt-2 h-4 w-20 rounded-full bg-[#0A192F]" />
              <div className="px-4 pt-5">
                <p className="text-[10px] font-bold uppercase tracking-widest text-brand">{t("hero3d_example")}</p>
                <p className="mt-1 font-display text-[15px] font-extrabold leading-tight text-brand-navy">Galaxy S24 · 8/256</p>
                <div className="mt-3 rounded-2xl bg-gradient-to-br from-brand-navy to-brand p-3 text-white">
                  <p className="text-[9px] font-semibold uppercase tracking-widest text-white/70">{t("est_label")}</p>
                  <p className="whitespace-nowrap font-display text-[16px] font-extrabold sm:text-[18px]">₹31,500 – ₹35,000</p>
                </div>
                {[["OLX", 92], ["Cashify", 78], ["Exchange", 70]].map(([n, w]) => (
                  <div key={n} className="mt-2.5 flex items-center gap-2">
                    <span className="w-14 text-[10px] font-semibold text-slate-500">{n}</span>
                    <span className="h-2 flex-1 rounded-full bg-slate-100"><span className="block h-2 rounded-full bg-brand/70" style={{ width: `${w}%` }} /></span>
                  </div>
                ))}
                <div className="mt-4 flex items-center justify-between gap-1">
                  <span className="whitespace-nowrap rounded-full bg-amber-100 px-1.5 py-0.5 text-[8px] font-bold text-amber-700">{t("confidence")}: {t("conf_Medium")}</span>
                  <span className="whitespace-nowrap rounded-full border border-brand/30 px-1.5 py-0.5 text-[8px] font-semibold text-brand">{t("check_quote")}</span>
                </div>
                <div className="mt-3 h-8 rounded-xl border border-dashed border-slate-200" />
              </div>
            </div>
          </div>
        </div>
        {/* glass cards */}
        <div className="vlx-layer vlx-card vlx-glass left-0 top-0 sm:-left-2 sm:top-[2%]" style={{ "--z": "110px", "--d": "0s" }}>
          <ShieldCheck className="h-5 w-5 text-emerald-500" />
          <div><p className="text-xs font-bold text-brand-navy">{t("hero3d_breakdown")}</p><p className="text-[10px] text-slate-500">{t("hero3d_breakdown_sub")}</p></div>
        </div>
        <div className="vlx-layer vlx-card vlx-glass bottom-[2%] right-0 sm:bottom-auto sm:top-[44%]" style={{ "--z": "150px", "--d": "1.2s" }}>
          <Store className="h-5 w-5 text-brand" />
          <div><p data-testid="hero-platform-count" className="font-display text-base font-extrabold text-brand-navy">{count}</p><p className="text-[10px] text-slate-500">{t("platforms_word")}</p></div>
        </div>
        <div className="vlx-layer vlx-card vlx-glass bottom-[6%] left-[6%] !hidden sm:!flex" style={{ "--z": "90px", "--d": "2.1s" }}>
          <MapPin className="h-5 w-5 text-rose-500" />
          <div><p className="text-xs font-bold text-brand-navy">{t("hero3d_dealers")}</p><p className="text-[10px] text-slate-500">{t("hero3d_dealers_sub")}</p></div>
        </div>
      </div>
    </div>
  );
};
